import { observer } from 'mobx-react-lite'
import React from 'react'
import PropTypes from 'prop-types'
import { Pagination } from 'antd'
import { useSmartTranslation } from '../../cls/TranslationContext'

/**
 * Props: LstItem, ids, children
 * @param {*} props
 * @returns <table> with <thead> and <tbody> elements
 * @LstTable
 */
/**
 * LstTable renders a data table with optional header, empty state, and pagination.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.id] – Optional HTML id for the container div.
 * @param {React.ComponentType} props.LstItem – The component used to render each row; receives props { id, isFirst, demo, selectedCurrency }.
 * @param {Object} props.store – MobX store with shape { ids: Array<string>, nr: number, pg: number, rpg: number, setPage: Function }.
 * @param {string} [props.className] – CSS class applied to the <table> element.
 * @param {React.ReactNode} [props.children] – Optional <thead> content (column headers).
 * @param {boolean} [props.demo=false] – If true, passes demo mode flag to each LstItem.
 * @param {string} [props.selectedCurrency='usd'] – Currency code passed to each LstItem.
 * @returns {JSX.Element|null}
 */
const LstTable = ({ id, LstItem, store, className, children, ...props }) => {
	const { t } = useSmartTranslation()
	if (!LstItem) return null

	// set the tour demo
	const demo = props?.demo || false
	const selectedCurrency = props?.selectedCurrency || 'usd'

	const { ids } = store
	const hasItems = ids?.length > 0
	const firstId = hasItems && ids[0]

	return (
		<div
			id={id}
			style={{ width: '100%' }}
			className={hasItems ? 'we-list-table' : ''}
		>
			{hasItems && (
				<table className={className}>
					{children && <thead>{children}</thead>}
					<tbody>
						{ids.map((id) => (
							<LstItem
								key={id}
								id={id}
								isFirst={firstId === id}
								demo={demo}
								selectedCurrency={selectedCurrency}
							/>
						))}
					</tbody>
				</table>
			)}
			{!hasItems && <span>{t('single.no-items')}</span>}
			{hasItems && (
				<Pagination
					style={{ marginTop: '20px' }}
					total={store.nr}
					current={store.pg}
					pageSize={store.rpg}
					onChange={(val) => {
						store.setPage(val)
						// TODO: scroll to top
					}}
					showPrevNextJumpers
					showTotal={(total) => t('_item.multiple', { count: total })}
				/>
			)}
		</div>
	)
}
LstTable.propTypes = {
	id: PropTypes.string,
	LstItem: PropTypes.elementType.isRequired,
	store: PropTypes.object.isRequired,
	className: PropTypes.string,
	children: PropTypes.node,
	demo: PropTypes.bool,
	selectedCurrency: PropTypes.string
}
export default observer(LstTable)
